import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

import { styled } from "@linaria/react";
import {
  CaretDownIcon,
  CaretRightIcon,
  CaretUpDownIcon,
  SortAscendingIcon,
  SortDescendingIcon,
} from "@phosphor-icons/react";
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef as TanstackColumnDef,
  type OnChangeFn as TanstackOnChangeFn,
  type Row as TanstackRow,
  type RowSelectionState as TanstackRowSelectionState,
  type SortingState as TanstackSortingState,
  type Table as TanstackTable,
} from "@tanstack/react-table";
import { match } from "ts-pattern";

import { withTheme } from "../theme/GalaxyTheme";
import type { PropsWithTheme } from "../theme/types";

import { getCssSizeValue } from "../utils/linaria";

import Button, { ButtonSize, ButtonVariant } from "../buttons/Button";
import Icon, { IconVariant } from "../icons/Icon";
import Text, { TextSize, TextVariant, TextWeight } from "../text/Text";

export enum TableDensity {
  SMALL = "SMALL",
  MEDIUM = "MEDIUM",
  LARGE = "LARGE",
}

export enum ColumnPin {
  LEFT = "LEFT",
  RIGHT = "RIGHT",
}

export enum ColumnAlign {
  LEFT = "LEFT",
  CENTER = "CENTER",
  RIGHT = "RIGHT",
}

export interface ColumnDef<TData, TValue = unknown> {
  id: string;

  // Accessor
  accessorFn?: (row: TData) => TValue;

  // Header
  header?: string | ((props: { table: TanstackTable<TData> }) => ReactNode);

  // Cell
  cell?: (props: { value: TValue; row: TanstackRow<TData> }) => ReactNode;

  // Sizing
  size?: number;
  minSize?: number;
  maxSize?: number;

  // Pinning
  pin?: ColumnPin;

  // Features
  enableSorting?: boolean;
  sortDescFirst?: boolean;

  // Styling
  align?: ColumnAlign;
  cellLoading?: () => ReactNode;
}

export interface EstimateColumnWidthOptions {
  minWidth?: number;
  maxWidth?: number;
  charWidth?: number;
  padding?: number;
}

/**
 * Estimate column width based on header name and sample data values.
 * Useful for auto-sizing table columns based on content.
 */
export const estimateColumnWidth = (
  columnName: string,
  sampleValues: string[],
  options: EstimateColumnWidthOptions = {},
): number => {
  const { minWidth = 80, maxWidth = 400, charWidth = 8, padding = 32 } = options;

  // Get the max length from header and sample values
  const headerLength = columnName.length;
  const maxValueLength = sampleValues.reduce((max, val) => Math.max(max, String(val).length), 0);
  const maxLength = Math.max(headerLength, maxValueLength);

  // Calculate width: characters * approximate char width + padding for cell padding
  const estimatedWidth = maxLength * charWidth + padding;

  // Clamp to min/max bounds
  return Math.max(minWidth, Math.min(maxWidth, estimatedWidth));
};

export interface InfiniteTableProps<TData> {
  // Required
  columns: ColumnDef<TData, unknown>[];
  data: TData[];

  // Row identity
  getRowId?: (row: TData, index: number) => string;

  // Dimensions
  height?: number | string;
  width?: number | string;
  fillWidth?: boolean;
  fillHeight?: boolean;
  density?: TableDensity;

  // Infinite scroll
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  fetchNextPage?: () => void;

  // Selection
  enableRowSelection?: boolean | ((row: TanstackRow<TData>) => boolean);
  enableMultiRowSelection?: boolean;
  rowSelection?: TanstackRowSelectionState;
  onRowSelectionChange?: TanstackOnChangeFn<TanstackRowSelectionState>;

  // Sorting
  enableSorting?: boolean;
  enableMultiSort?: boolean;
  sorting?: TanstackSortingState;
  onSortingChange?: TanstackOnChangeFn<TanstackSortingState>;
  manualSorting?: boolean;

  // States
  isLoading?: boolean;
  isError?: boolean;
  loadingRowCount?: number;

  // Content
  contentWhenEmpty?: ReactNode;
  contentWhenError?: ReactNode | ((error?: Error) => ReactNode);

  // Styling
  noHeader?: boolean;
  noLastRowBorder?: boolean;
  noLastRowPadding?: boolean;

  // Callbacks
  onRowClick?: (row: TanstackRow<TData>, event: React.MouseEvent) => void;
  onRowDoubleClick?: (row: TanstackRow<TData>, event: React.MouseEvent) => void;
  onRowExpand?: (row: TanstackRow<TData>) => ReactNode;

  // Advanced
  onTableReady?: (table: TanstackTable<TData>) => void;
}

const EXPAND_COLUMN_ID = "__expand__";
const EXPAND_COLUMN_WIDTH = 44;
const TABLE_DENSITY_TO_BUTTON_SIZE_MAP: Record<TableDensity, ButtonSize> = {
  [TableDensity.SMALL]: ButtonSize.SMALL,
  [TableDensity.MEDIUM]: ButtonSize.SMALL,
  [TableDensity.LARGE]: ButtonSize.MEDIUM,
};

const TableOuterContainer = withTheme(styled.div<
  PropsWithTheme<{
    $width?: number | string;
    $height?: number | string;
    $fillWidth?: boolean;
    $fillHeight?: boolean;
  }>
>`
  width: ${({ $width, $fillWidth }) => {
    if ($fillWidth) return "100%";
    if ($width) return getCssSizeValue($width);
    return "auto";
  }};
  height: ${({ $height, $fillHeight }) => {
    if ($fillHeight) return "100%";
    if ($height) return getCssSizeValue($height);
    return "auto";
  }};
  position: relative;
  display: flex;
  flex-direction: column;
  border-radius: 5px;
  overflow: hidden;
`);

const TableScrollContainer = styled.div<{ $noLastRowPadding?: boolean }>`
  flex: 1;
  overflow: auto;
  position: relative;
  min-height: 0;
  padding-bottom: ${({ $noLastRowPadding }) => ($noLastRowPadding ? "0" : "8px")};
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  table-layout: fixed;
`;

const TableHead = styled.thead`
  display: table-header-group;
  position: sticky;
  top: 0px;
  z-index: 20;
`;

const TableBody = styled.tbody`
  display: block;
`;

const TableRow = withTheme(styled.tr<
  PropsWithTheme<{
    $isClickable?: boolean;
    $isSelected?: boolean;
    $density?: TableDensity;
    $isLastRow?: boolean;
    $isExpanded?: boolean;
  }>
>`
  cursor: ${({ $isClickable }) => ($isClickable ? "pointer" : "default")};
  transition: background-color 0.075s ease-in-out;
  display: flex;
  width: 100%;
  height: ${({ $density }) =>
    match($density)
      .with(TableDensity.SMALL, () => "32px")
      .with(TableDensity.MEDIUM, () => "48px")
      .with(TableDensity.LARGE, () => "56px")
      .with(undefined, () => "48px")
      .exhaustive()};

  & > td {
    background-color: ${({ $isSelected, theme }) =>
      $isSelected ? theme.color.background.secondary : theme.color.background.primary};
    border-bottom: ${({ $isExpanded, $isLastRow, theme }) => {
      if ($isExpanded || $isLastRow) return "none";
      return `0.5px solid ${theme.color.border.primary}`;
    }};
  }

  &:hover > td {
    background-color: ${({ $isClickable, $isSelected, theme }) => {
      if ($isSelected) return theme.color.background.secondary;
      if ($isClickable) return theme.color.background.tertiary;
      return theme.color.background.primary;
    }};
  }
`);

const TableHeaderRow = withTheme(styled.tr<PropsWithTheme>`
  display: flex;
  width: 100%;
`);

const TableHeaderCell = withTheme(styled.th<
  PropsWithTheme<{
    $width?: number;
    $minWidth?: number;
    $maxWidth?: number;
    $pin?: ColumnPin;
    $pinnedOffset?: number;
    $align?: ColumnAlign;
    $isSortable?: boolean;
    $density?: TableDensity;
    $isExpandCell?: boolean;
  }>
>`
  padding: ${({ $density, $isExpandCell }) => {
    if ($isExpandCell) return "4px 6px";

    return `8px ${match($density)
      .with(TableDensity.SMALL, () => "12px")
      .with(TableDensity.MEDIUM, () => "16px")
      .with(TableDensity.LARGE, () => "20px")
      .with(undefined, () => "16px")
      .exhaustive()}`;
  }};
  flex: ${({ $width }) => {
    if ($width) return `0 0 ${$width}px`;
    return "1 1 0";
  }};
  min-width: ${({ $minWidth }) => {
    if ($minWidth) return `${$minWidth}px`;
    return "0";
  }};
  max-width: ${({ $maxWidth }) => {
    if ($maxWidth) return `${$maxWidth}px`;
    return "none";
  }};
  vertical-align: middle;
  text-align: ${({ $align }) =>
    match($align)
      .with(ColumnAlign.LEFT, () => "left")
      .with(ColumnAlign.CENTER, () => "center")
      .with(ColumnAlign.RIGHT, () => "right")
      .with(undefined, () => "left")
      .exhaustive()};
  border-bottom: 0.5px solid ${({ theme }) => theme.color.border.primary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  background-color: ${({ theme }) => theme.color.background.primary};
  display: flex;
  align-items: center;
  justify-content: ${({ $align }) =>
    match($align)
      .with(ColumnAlign.LEFT, () => "flex-start")
      .with(ColumnAlign.CENTER, () => "center")
      .with(ColumnAlign.RIGHT, () => "flex-end")
      .with(undefined, () => "flex-start")
      .exhaustive()};
  cursor: ${({ $isSortable }) => {
    if ($isSortable) return "pointer";
    return "default";
  }};

  /* Always sticky for vertical scrolling */
  position: sticky;
  position: -webkit-sticky; /* Safari support */
  top: 0;
  z-index: ${({ $pin }) => {
    if ($pin) return 25; /* Pinned columns above regular headers */
    return 20; /* Headers above body rows */
  }};
  left: ${({ $pin, $pinnedOffset }) => {
    if ($pin === ColumnPin.LEFT && $pinnedOffset !== undefined) return `${$pinnedOffset}px`;
    return "auto";
  }};
  right: ${({ $pin, $pinnedOffset }) => {
    if ($pin === ColumnPin.RIGHT && $pinnedOffset !== undefined) return `${$pinnedOffset}px`;
    return "auto";
  }};

  border-right: ${({ $pin, theme }) => {
    if ($pin === ColumnPin.LEFT) return `0.5px solid ${theme.color.border.primary}`;
    return "none";
  }};
  border-left: ${({ $pin, theme }) => {
    if ($pin === ColumnPin.RIGHT) return `0.5px solid ${theme.color.border.primary}`;
    return "none";
  }};
`);

const TableDataCell = withTheme(styled.td<
  PropsWithTheme<{
    $width?: number;
    $minWidth?: number;
    $maxWidth?: number;
    $pin?: ColumnPin;
    $pinnedOffset?: number;
    $align?: ColumnAlign;
    $density?: TableDensity;
    $isExpandCell?: boolean;
  }>
>`
  padding: ${({ $density, $isExpandCell }) => {
    if ($isExpandCell) return "4px 6px";

    return `8px ${match($density)
      .with(TableDensity.SMALL, () => "12px")
      .with(TableDensity.MEDIUM, () => "16px")
      .with(TableDensity.LARGE, () => "20px")
      .with(undefined, () => "16px")
      .exhaustive()}`;
  }};
  color: ${({ theme }) => theme.color.text.primary};
  vertical-align: middle;
  flex: ${({ $width }) => {
    if ($width) return `0 0 ${$width}px`;
    return "1 1 0";
  }};
  min-width: ${({ $minWidth }) => {
    if ($minWidth) return `${$minWidth}px`;
    return "0";
  }};
  max-width: ${({ $maxWidth }) => {
    if ($maxWidth) return `${$maxWidth}px`;
    return "none";
  }};
  height: 100%;
  text-align: ${({ $align }) =>
    match($align)
      .with(ColumnAlign.LEFT, () => "left")
      .with(ColumnAlign.CENTER, () => "center")
      .with(ColumnAlign.RIGHT, () => "right")
      .with(undefined, () => "left")
      .exhaustive()};
  justify-content: ${({ $align }) =>
    match($align)
      .with(ColumnAlign.LEFT, () => "flex-start")
      .with(ColumnAlign.CENTER, () => "center")
      .with(ColumnAlign.RIGHT, () => "flex-end")
      .with(undefined, () => "flex-start")
      .exhaustive()};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: flex;
  align-items: center;

  background-color: ${({ $pin, theme }) => {
    if ($pin) return theme.color.background.primary;
    return "transparent";
  }};

  position: ${({ $pin }) => {
    if ($pin) return "sticky";
    return "static";
  }};
  z-index: ${({ $pin }) => {
    if ($pin) return 5;
    return "auto";
  }};
  left: ${({ $pin, $pinnedOffset }) => {
    if ($pin === ColumnPin.LEFT && $pinnedOffset !== undefined) return `${$pinnedOffset}px`;
    return "auto";
  }};
  right: ${({ $pin, $pinnedOffset }) => {
    if ($pin === ColumnPin.RIGHT && $pinnedOffset !== undefined) return `${$pinnedOffset}px`;
    return "auto";
  }};

  border-right: ${({ $pin, theme }) => {
    if ($pin === ColumnPin.LEFT) return `0.5px solid ${theme.color.border.primary}`;
    return "none";
  }};
  border-left: ${({ $pin, theme }) => {
    if ($pin === ColumnPin.RIGHT) return `0.5px solid ${theme.color.border.primary}`;
    return "none";
  }};
`);

const HeaderContentWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SortIconWrapper = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
`;

const EmptyContentWrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;

  & > * {
    pointer-events: auto;
  }
`;

const LoadingRowWrapper = withTheme(styled.tr<PropsWithTheme<{ $density?: TableDensity }>>`
  display: flex;
  width: 100%;
  height: ${({ $density }) =>
    match($density)
      .with(TableDensity.SMALL, () => "32px")
      .with(TableDensity.MEDIUM, () => "48px")
      .with(TableDensity.LARGE, () => "56px")
      .with(undefined, () => "48px")
      .exhaustive()};

  & > td {
    border-bottom: 0.5px solid ${({ theme }) => theme.color.border.primary};
    background-color: ${({ theme }) => theme.color.background.primary};
  }
`);

const ExpandedSpacerRow = styled.tr<{ $height: number }>`
  display: block;
  width: 100%;
  height: ${({ $height }) => `${$height}px`};
  pointer-events: none;
`;

const ExpandedSpacerCell = withTheme(styled.td<PropsWithTheme>`
  display: block;
  width: 100%;
  height: 100%;
  padding: 0;
  border: none;
  background: transparent;
  box-sizing: border-box;
`);

const ExpandedOverlayLayer = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  overflow: hidden;
  z-index: 30;
`;

const ExpandedOverlay = withTheme(styled.div<
  PropsWithTheme<{
    $top: number;
    $left: number;
    $width: number;
    $isLastRow?: boolean;
  }>
>`
  position: absolute;
  top: ${({ $top }) => `${$top}px`};
  left: ${({ $left }) => `${$left}px`};
  width: ${({ $width }) => `${$width}px`};
  display: grid;
  grid-template-columns: ${EXPAND_COLUMN_WIDTH}px minmax(0, 1fr);
  box-sizing: border-box;
  background-color: ${({ theme }) => theme.color.background.primary};
  border-bottom: ${({ $isLastRow, theme }) =>
    $isLastRow ? "none" : `0.5px solid ${theme.color.border.primary}`};
  overflow: hidden;
  contain: layout paint;
  overscroll-behavior: auto;
  pointer-events: auto;
`);

const ExpandedGutter = withTheme(styled.div<PropsWithTheme>`
  background-color: ${({ theme }) => theme.color.background.primary};
  border-right: 0.5px solid ${({ theme }) => theme.color.border.primary};
  min-width: ${EXPAND_COLUMN_WIDTH}px;
`);

const ExpandedBody = withTheme(styled.div<PropsWithTheme>`
  min-width: 0;
  width: 100%;
  border-top: 0.5px solid ${({ theme }) => theme.color.border.primary};
`);

const ExpandedContent = styled.div`
  width: 100%;
  box-sizing: border-box;
  overscroll-behavior: auto;
`;

function InfiniteTable<TData>(props: InfiniteTableProps<TData>) {
  const {
    columns,
    data,
    getRowId,
    height,
    width,
    fillWidth,
    fillHeight,
    density = TableDensity.MEDIUM,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    enableRowSelection,
    enableMultiRowSelection,
    rowSelection,
    onRowSelectionChange,
    enableSorting = false,
    enableMultiSort = false,
    sorting,
    onSortingChange,
    manualSorting = false,
    isLoading = false,
    isError = false,
    loadingRowCount = 5,
    contentWhenEmpty,
    contentWhenError,
    noHeader = false,
    noLastRowBorder = false,
    noLastRowPadding = false,
    onRowClick,
    onRowDoubleClick,
    onRowExpand,
    onTableReady,
  } = props;

  const outerContainerRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const expandedOverlayRef = useRef<HTMLDivElement>(null);
  const queueWheelScrollRef = useRef<((deltaX: number, deltaY: number) => boolean) | null>(null);
  const syncExpandedOverlayLayoutRafRef = useRef<number | null>(null);
  const [expandedRowId, setExpandedRowId] = useState<string | null>(null);
  const [expandedOverlayLayout, setExpandedOverlayLayout] = useState<{
    top: number;
    left: number;
    width: number;
    height: number;
  } | null>(null);

  const resolvedColumns = useMemo<ColumnDef<TData, unknown>[]>(() => {
    if (!onRowExpand) {
      return columns;
    }

    return [
      {
        id: EXPAND_COLUMN_ID,
        header: () => null,
        size: EXPAND_COLUMN_WIDTH,
        minSize: EXPAND_COLUMN_WIDTH,
        maxSize: EXPAND_COLUMN_WIDTH,
        pin: ColumnPin.LEFT,
        align: ColumnAlign.CENTER,
        cell: ({ row }) => {
          const isExpanded = expandedRowId === row.id;
          const buttonSize = TABLE_DENSITY_TO_BUTTON_SIZE_MAP[density];

          return (
            <Button
              aria-expanded={isExpanded}
              aria-label={isExpanded ? "Collapse row" : "Expand row"}
              icon={isExpanded ? CaretDownIcon : CaretRightIcon}
              onClick={(event) => {
                event.stopPropagation();
                setExpandedRowId((currentRowId) => (currentRowId === row.id ? null : row.id));
              }}
              variant={ButtonVariant.TERTIARY}
              size={buttonSize}
            />
          );
        },
        cellLoading: () => null,
      },
      ...columns,
    ];
  }, [columns, density, expandedRowId, onRowExpand]);

  // Calculate pinned column offsets
  const pinnedOffsets = useMemo(() => {
    const offsets: Map<string, number> = new Map();
    let leftOffset = 0;
    let rightOffset = 0;

    // Left pinned columns
    resolvedColumns.forEach((col) => {
      if (col.pin === ColumnPin.LEFT) {
        offsets.set(col.id, leftOffset);
        leftOffset += col.size ?? 150;
      }
    });

    // Right pinned columns (iterate in reverse)
    [...resolvedColumns].reverse().forEach((col) => {
      if (col.pin === ColumnPin.RIGHT) {
        offsets.set(col.id, rightOffset);
        rightOffset += col.size ?? 150;
      }
    });

    return offsets;
  }, [resolvedColumns]);

  // Map our ColumnDef to TanStack's format
  const tanstackColumns = useMemo<TanstackColumnDef<TData, unknown>[]>(
    () =>
      resolvedColumns.map((col) => ({
        id: col.id,
        accessorFn: col.accessorFn,
        header: ({ table }) => {
          if (typeof col.header === "function") {
            return col.header({ table });
          }
          return (
            <HeaderContentWrapper>
              <Text
                variant={TextVariant.TERTIARY}
                weight={TextWeight.MEDIUM}
                size={TextSize.BODY_SM}
              >
                {col.header ?? col.id}
              </Text>
            </HeaderContentWrapper>
          );
        },
        cell: (info) => {
          if (col.cell) {
            return col.cell({ value: info.getValue(), row: info.row });
          }
          const value = info.getValue();
          if (typeof value === "string") {
            return <Text size={TextSize.BODY_SM}>{value}</Text>;
          }
          if (
            typeof value === "number" ||
            typeof value === "boolean" ||
            typeof value === "bigint"
          ) {
            return <Text size={TextSize.BODY_SM}>{String(value)}</Text>;
          }
          return <Text size={TextSize.BODY_SM}>{""}</Text>;
        },
        size: col.size,
        minSize: col.minSize,
        maxSize: col.maxSize,
        enableSorting: col.enableSorting ?? false,
        sortDescFirst: col.sortDescFirst,
        meta: {
          pin: col.pin,
          align: col.align,
          cellLoading: col.cellLoading,
        },
      })),
    [resolvedColumns],
  );

  // Create TanStack Table instance
  const table = useReactTable({
    data,
    columns: tanstackColumns,
    getRowId: getRowId ? (row, index) => getRowId(row, index) : undefined,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: enableSorting && !manualSorting ? getSortedRowModel() : undefined,
    state: {
      ...(sorting !== undefined && { sorting }),
      ...(rowSelection !== undefined && { rowSelection }),
    },
    ...(onSortingChange !== undefined && { onSortingChange }),
    ...(onRowSelectionChange !== undefined && { onRowSelectionChange }),
    enableRowSelection: enableRowSelection,
    enableMultiRowSelection: enableMultiRowSelection,
    enableSorting: enableSorting,
    enableMultiSort: enableMultiSort,
    manualSorting: manualSorting,
  });

  // Expose table instance
  useEffect(() => {
    onTableReady?.(table);
  }, [table, onTableReady]);

  // Get rows from table
  const { rows } = table.getRowModel();
  const expandedRow = expandedRowId ? rows.find((row) => row.id === expandedRowId) : undefined;
  const isExpandedRowLast = expandedRow ? rows[rows.length - 1]?.id === expandedRow.id : false;

  useEffect(() => {
    if (!onRowExpand && expandedRowId !== null) {
      setExpandedRowId(null);
    }
  }, [expandedRowId, onRowExpand]);

  useEffect(() => {
    if (expandedRowId === null) return;

    const rowStillExists = rows.some((row) => row.id === expandedRowId);
    if (!rowStillExists) {
      setExpandedRowId(null);
    }
  }, [expandedRowId, rows]);

  const syncExpandedOverlayLayout = useCallback(() => {
    if (!expandedRow || !onRowExpand) {
      setExpandedOverlayLayout(null);
      return;
    }

    const outerContainer = outerContainerRef.current;
    const scrollContainer = scrollContainerRef.current;
    const overlayElement = expandedOverlayRef.current;
    if (!outerContainer || !scrollContainer || !overlayElement) return;

    const expandedRowAnchor = scrollContainer.querySelector<HTMLTableRowElement>(
      '[data-gx-expanded-row-anchor="true"]',
    );
    if (!expandedRowAnchor) return;

    const outerRect = outerContainer.getBoundingClientRect();
    const scrollRect = scrollContainer.getBoundingClientRect();
    const rowRect = expandedRowAnchor.getBoundingClientRect();
    const overlayRect = overlayElement.getBoundingClientRect();

    const nextLayout = {
      top: rowRect.bottom - outerRect.top,
      left: scrollRect.left - outerRect.left,
      width: scrollContainer.clientWidth,
      height: overlayRect.height,
    };

    setExpandedOverlayLayout((currentLayout) => {
      if (
        currentLayout &&
        currentLayout.top === nextLayout.top &&
        currentLayout.left === nextLayout.left &&
        currentLayout.width === nextLayout.width &&
        currentLayout.height === nextLayout.height
      ) {
        return currentLayout;
      }

      return nextLayout;
    });
  }, [expandedRow, onRowExpand]);

  const scheduleSyncExpandedOverlayLayout = useCallback(() => {
    if (syncExpandedOverlayLayoutRafRef.current !== null) return;

    syncExpandedOverlayLayoutRafRef.current = requestAnimationFrame(() => {
      syncExpandedOverlayLayoutRafRef.current = null;
      syncExpandedOverlayLayout();
    });
  }, [syncExpandedOverlayLayout]);

  useLayoutEffect(() => {
    syncExpandedOverlayLayout();

    return () => {
      if (syncExpandedOverlayLayoutRafRef.current !== null) {
        cancelAnimationFrame(syncExpandedOverlayLayoutRafRef.current);
        syncExpandedOverlayLayoutRafRef.current = null;
      }
    };
  }, [syncExpandedOverlayLayout]);

  useEffect(() => {
    if (!expandedRow || !onRowExpand) return;

    const scrollContainer = scrollContainerRef.current;
    const overlayElement = expandedOverlayRef.current;
    if (!scrollContainer || !overlayElement) return;

    scrollContainer.addEventListener("scroll", scheduleSyncExpandedOverlayLayout, {
      passive: true,
    });
    window.addEventListener("resize", scheduleSyncExpandedOverlayLayout);

    if (typeof ResizeObserver === "undefined") {
      return () => {
        scrollContainer.removeEventListener("scroll", scheduleSyncExpandedOverlayLayout);
        window.removeEventListener("resize", scheduleSyncExpandedOverlayLayout);
      };
    }

    const resizeObserver = new ResizeObserver(() => {
      scheduleSyncExpandedOverlayLayout();
    });

    resizeObserver.observe(scrollContainer);
    resizeObserver.observe(overlayElement);

    return () => {
      scrollContainer.removeEventListener("scroll", scheduleSyncExpandedOverlayLayout);
      window.removeEventListener("resize", scheduleSyncExpandedOverlayLayout);
      resizeObserver.disconnect();
    };
  }, [expandedRow, onRowExpand, scheduleSyncExpandedOverlayLayout]);

  // Infinite scroll trigger
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container || !fetchNextPage) return;

    const onScroll = () => {
      const { scrollHeight, scrollTop, clientHeight } = container;
      if (scrollHeight - scrollTop - clientHeight < 500 && hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    };

    container.addEventListener("scroll", onScroll);
    return () => container.removeEventListener("scroll", onScroll);
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  // Chrome wheel scroll fix - Chrome doesn't natively scroll in this layout context
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    let pendingScroll = { deltaX: 0, deltaY: 0 };
    let rafId: number | null = null;

    const applyScroll = () => {
      if (pendingScroll.deltaY !== 0 || pendingScroll.deltaX !== 0) {
        container.scrollTop += pendingScroll.deltaY;
        container.scrollLeft += pendingScroll.deltaX;
        pendingScroll = { deltaX: 0, deltaY: 0 };
      }
      rafId = null;
    };

    const queueWheelScroll = (deltaX: number, deltaY: number) => {
      // When fetching next page, prevent scrolling past the current content
      if (isFetchingNextPage && deltaY > 0) {
        const isNearBottom =
          container.scrollHeight - container.scrollTop - container.clientHeight < 200;
        if (isNearBottom) {
          return false;
        }
      }

      // Only handle if this container can scroll in the wheel direction
      const canScrollY =
        (deltaY > 0 && container.scrollTop < container.scrollHeight - container.clientHeight) ||
        (deltaY < 0 && container.scrollTop > 0);
      const canScrollX =
        (deltaX > 0 && container.scrollLeft < container.scrollWidth - container.clientWidth) ||
        (deltaX < 0 && container.scrollLeft > 0);

      if (canScrollY || canScrollX) {
        // Accumulate scroll deltas
        pendingScroll.deltaY += deltaY;
        pendingScroll.deltaX += deltaX;

        // Use requestAnimationFrame to batch scroll updates
        if (rafId === null) {
          rafId = requestAnimationFrame(applyScroll);
        }

        return true;
      }

      return false;
    };

    queueWheelScrollRef.current = queueWheelScroll;

    const onWheel = (e: WheelEvent) => {
      queueWheelScroll(e.deltaX, e.deltaY);
    };

    container.addEventListener("wheel", onWheel, { passive: true });
    return () => {
      queueWheelScrollRef.current = null;
      container.removeEventListener("wheel", onWheel);
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
    };
  }, [isFetchingNextPage]);

  const handleExpandedOverlayWheel = useCallback((event: React.WheelEvent<HTMLDivElement>) => {
    const handled = queueWheelScrollRef.current?.(event.deltaX, event.deltaY) ?? false;
    if (handled) {
      event.preventDefault();
      event.stopPropagation();
    }
  }, []);

  // Render loading rows
  const renderLoadingRows = () => {
    return Array.from({ length: loadingRowCount }).map((_, rowIndex) => (
      <LoadingRowWrapper key={`loading-${rowIndex}`} $density={density}>
        {resolvedColumns.map((col) => (
          <TableDataCell
            key={col.id}
            $width={col.size}
            $minWidth={col.minSize}
            $maxWidth={col.maxSize}
            $pin={col.pin}
            $pinnedOffset={pinnedOffsets.get(col.id)}
            $align={col.align}
            $density={density}
          >
            {col.cellLoading ? col.cellLoading() : null}
          </TableDataCell>
        ))}
      </LoadingRowWrapper>
    ));
  };

  // Determine states
  const isEmpty = data.length === 0 && !isLoading;
  const showLoading = isLoading && data.length === 0;
  const showError = isError && data.length === 0;
  return (
    <TableOuterContainer
      ref={outerContainerRef}
      $width={width}
      $height={height}
      $fillWidth={fillWidth}
      $fillHeight={fillHeight}
    >
      <TableScrollContainer ref={scrollContainerRef} $noLastRowPadding={noLastRowPadding}>
        <StyledTable>
          {!noHeader && (
            <TableHead>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableHeaderRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    const columnMeta = header.column.columnDef.meta as
                      | { pin?: ColumnPin; align?: ColumnAlign }
                      | undefined;
                    const canSort = header.column.getCanSort();

                    return (
                      <TableHeaderCell
                        key={header.id}
                        $width={header.column.columnDef.size}
                        $minWidth={header.column.columnDef.minSize}
                        $maxWidth={header.column.columnDef.maxSize}
                        $pin={columnMeta?.pin}
                        $pinnedOffset={pinnedOffsets.get(header.column.id)}
                        $align={columnMeta?.align}
                        $isSortable={canSort}
                        $density={density}
                        $isExpandCell={header.column.id === EXPAND_COLUMN_ID}
                        onClick={canSort ? header.column.getToggleSortingHandler() : undefined}
                      >
                        <HeaderContentWrapper>
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {canSort && (
                            <SortIconWrapper>
                              {header.column.getIsSorted() === "asc" ? (
                                <Icon
                                  component={SortAscendingIcon}
                                  size={12}
                                  variant={IconVariant.TERTIARY}
                                />
                              ) : header.column.getIsSorted() === "desc" ? (
                                <Icon
                                  component={SortDescendingIcon}
                                  size={12}
                                  variant={IconVariant.TERTIARY}
                                />
                              ) : (
                                <Icon
                                  component={CaretUpDownIcon}
                                  size={12}
                                  variant={IconVariant.TERTIARY}
                                />
                              )}
                            </SortIconWrapper>
                          )}
                        </HeaderContentWrapper>
                      </TableHeaderCell>
                    );
                  })}
                </TableHeaderRow>
              ))}
            </TableHead>
          )}

          <TableBody>
            {showLoading ? (
              renderLoadingRows()
            ) : showError ? (
              <tr>
                <td colSpan={resolvedColumns.length}>
                  {typeof contentWhenError === "function"
                    ? contentWhenError()
                    : (contentWhenError ?? (
                        <Text variant={TextVariant.ERROR}>Error loading data</Text>
                      ))}
                </td>
              </tr>
            ) : (
              rows.map((row, index) => {
                const isSelected = row.getIsSelected();
                const isExpanded = onRowExpand !== undefined && row.id === expandedRowId;
                const isLastRow = index === rows.length - 1;

                return (
                  <React.Fragment key={row.id}>
                    <TableRow
                      data-gx-expanded-row-anchor={isExpanded ? "true" : undefined}
                      $isClickable={!!onRowClick}
                      $isSelected={isSelected}
                      $density={density}
                      $isExpanded={isExpanded}
                      $isLastRow={noLastRowBorder && isLastRow && !isExpanded}
                      onClick={onRowClick ? (e) => onRowClick(row, e) : undefined}
                      onDoubleClick={onRowDoubleClick ? (e) => onRowDoubleClick(row, e) : undefined}
                    >
                      {row.getVisibleCells().map((cell) => {
                        const columnMeta = cell.column.columnDef.meta as
                          | { pin?: ColumnPin; align?: ColumnAlign }
                          | undefined;

                        return (
                          <TableDataCell
                            key={cell.id}
                            $width={cell.column.columnDef.size}
                            $minWidth={cell.column.columnDef.minSize}
                            $maxWidth={cell.column.columnDef.maxSize}
                            $pin={columnMeta?.pin}
                            $pinnedOffset={pinnedOffsets.get(cell.column.id)}
                            $align={columnMeta?.align}
                            $density={density}
                            $isExpandCell={cell.column.id === EXPAND_COLUMN_ID}
                          >
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </TableDataCell>
                        );
                      })}
                    </TableRow>

                    {isExpanded && (
                      <ExpandedSpacerRow $height={expandedOverlayLayout?.height ?? 0}>
                        <ExpandedSpacerCell colSpan={resolvedColumns.length} />
                      </ExpandedSpacerRow>
                    )}
                  </React.Fragment>
                );
              })
            )}

            {/* Loading more indicator */}
            {isFetchingNextPage && renderLoadingRows()}
          </TableBody>
        </StyledTable>
      </TableScrollContainer>

      {expandedRow && onRowExpand && (
        <ExpandedOverlayLayer>
          <ExpandedOverlay
            ref={expandedOverlayRef}
            $isLastRow={noLastRowBorder && isExpandedRowLast}
            $left={expandedOverlayLayout?.left ?? 0}
            $top={expandedOverlayLayout?.top ?? 0}
            $width={expandedOverlayLayout?.width ?? scrollContainerRef.current?.clientWidth ?? 0}
            onWheel={handleExpandedOverlayWheel}
            style={{ visibility: expandedOverlayLayout ? "visible" : "hidden" }}
          >
            <ExpandedGutter />
            <ExpandedBody>
              <ExpandedContent>{onRowExpand(expandedRow)}</ExpandedContent>
            </ExpandedBody>
          </ExpandedOverlay>
        </ExpandedOverlayLayer>
      )}

      {/* Empty state */}
      {isEmpty && contentWhenEmpty && <EmptyContentWrapper>{contentWhenEmpty}</EmptyContentWrapper>}
    </TableOuterContainer>
  );
}

export default InfiniteTable;
export type { TanstackRow as Row };
